import { useContext } from 'react';
import { defaultDataIdFromObject, useMutation } from '@apollo/client';
import { useIntl, defineMessages } from 'react-intl';
import { get } from 'lodash';
import * as immutable from 'object-path-immutable';

import TASK_DELETE from 'components/projects/taskList/gql/taskDelete.graphql';
import { GET_TASK_LIST_HEADER } from 'components/projects/taskList/gql/getTaskList.graphql';
import {
  GET_TASK_LIST_SECTIONLESS,
  SECTIONLESS_TASKS_PER_PAGE,
} from 'components/projects/taskList/hooks/useGetSectionlessTasks';
import { TaskListSectionsFragment } from 'components/projects/taskList/gql/fragments';
import TemplateContext from 'components/projects/taskList/templates/TemplateContext';
import { getGqlErrors } from 'components/projects/taskList/helpers';
import toast from 'helpers/toast';

defineMessages({
  deleteError: {
    id: 'Projects.taskList.taskDeleteModal.deleteError',
    defaultMessage: 'An error occured deleting this field.',
    description: 'message when an error occurs deleting a task',
  },
});

const useTaskDelete = (taskListId) => {
  const { formatMessage } = useIntl();

  const { template, deleteTask: deleteTemplateTask } =
    useContext(TemplateContext) || {};

  const [deleteTaskMutation] = useMutation(TASK_DELETE, {
    onError: () => {
      toast.error(
        formatMessage({ id: 'Projects.taskList.taskDeleteModal.deleteError' }),
      );
    },
    refetchQueries: [
      {
        query: GET_TASK_LIST_HEADER,
        variables: { id: taskListId },
      },
      'GetTaskListTemplateHeaderInfo',
    ],
  });

  const deleteTask = (task) => {
    if (template) {
      deleteTemplateTask(task);
    } else {
      deleteTaskMutation({
        variables: {
          input: {
            id: task.id,
          },
        },
        update: (cache, { data }) => {
          const gqlErrors = getGqlErrors(data);

          if (gqlErrors.length) {
            toast(gqlErrors[0].message);
            return;
          }

          const cacheId = defaultDataIdFromObject({
            id: taskListId,
            __typename: 'TaskList',
          });

          if (!task.taskListSection) {
            const cachedData = cache.readQuery({
              query: GET_TASK_LIST_SECTIONLESS,
              variables: {
                id: taskListId,
                first: SECTIONLESS_TASKS_PER_PAGE,
              },
            });
            const tasks = get(cachedData, 'viewer.taskList.tasks.nodes', []);

            const taskIndex = tasks
              .map((cachedTask) => cachedTask.id)
              .indexOf(task.id);

            if (taskIndex >= 0) {
              const updatedSectionlessTasksData = immutable.del(
                cachedData,
                `viewer.taskList.tasks.nodes.${taskIndex}`,
              );

              cache.writeQuery({
                query: GET_TASK_LIST_SECTIONLESS,
                variables: {
                  id: taskListId,
                  first: SECTIONLESS_TASKS_PER_PAGE,
                },
                data: updatedSectionlessTasksData,
              });
            }
          } else {
            const cachedData = cache.readFragment({
              id: cacheId,
              fragment: TaskListSectionsFragment,
              fragmentName: 'TaskListSectionsFragment',
            });
            const sections = get(cachedData, 'taskListSections.nodes', []);
            const sectionIndex = sections
              .map((section) => section.id)
              .indexOf(task.taskListSection.id);
            const taskIndex =
              sectionIndex >= 0
                ? sections[sectionIndex].tasks.nodes
                    .map((cachedTask) => cachedTask.id)
                    .indexOf(task.id)
                : -1;

            if (sectionIndex >= 0 && taskIndex >= 0) {
              const updatedSectionTasksData = immutable.del(
                cachedData,
                `taskListSections.nodes.${sectionIndex}.tasks.nodes.${taskIndex}`,
              );

              cache.writeFragment({
                id: cacheId,
                fragment: TaskListSectionsFragment,
                fragmentName: 'TaskListSectionsFragment',
                data: updatedSectionTasksData,
              });
            }
          }
        },
      });
    }
  };

  return { deleteTask };
};

export default useTaskDelete;
